import { useFormik } from 'formik';
import useTranslate from 'hooks/useTranslate';
import { ReactNode, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { generateYearOptions } from 'utils';
import * as Yup from 'yup';
import ActionMenu from '../ActionMenu';
import AddMoreButton from '../AddMoreButton';
import InfoCard from '../InfoCard';
import useResumeStore from 'store/resume';
import { TEXTAREA_TEXT_LIMIT } from 'constants';

interface AchievementsInterface {
	title: string;
	year: string;
	description: string;
	logo: string;
}

const Achievements = ({
	data,
	slug,
	handleAddMoreClick,
	handleRemoveClick,
	onHandleChange,
	handleFileChange,
	handleModalVisibility,
	handleTitleChange,
	showAwardImage,
	checkLimitExceed,
}: any) => {
	const { translate } = useTranslate();
	const fieldVisibility = useResumeStore(
		(state: any) => state?.resumeData?.manageTemplateFields?.[slug] || false,
	);
	let validationSchema = Yup.object().shape({
		title: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.title') }))
			.max(
				100,
				translate('Validation.max', {
					field: translate('PlaceHolder.title'),
					number: `100`,
				}),
			),
		year: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.year') }),
		),
		description: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.description') }))
			.min(
				50,
				translate('Validation.min', { field: translate('PlaceHolder.description'), number: '50' }),
			)
			.max(
				TEXTAREA_TEXT_LIMIT,
				translate('Validation.max', {
					field: translate('PlaceHolder.description'),
					number: `${TEXTAREA_TEXT_LIMIT}`,
				}),
			),
	});

	if (showAwardImage) {
		validationSchema = validationSchema.shape({
			logo: Yup.string(),
		});
	}

	const formik = useFormik({
		initialValues: data?.length
			? data[data.length - 1]
			: {
					title: '',
					year: '',
					description: '',
					logo: '',
				},
		enableReinitialize: true,
		validationSchema: validationSchema,
		onSubmit: (_values: any) => {
			handleAddMoreClick(slug);
			formik.resetForm();
		},
	});

	const renderError = <T extends keyof AchievementsInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const checkLimits = useMemo(() => {
		return checkLimitExceed(slug);
	}, [data?.length, fieldVisibility]);

	return (
		<div className="resume-info">
			<Form onSubmit={formik.handleSubmit} autoComplete="off">
				<div className="build-resume-edit-header">
					<ActionMenu
						data={data}
						handleRemoveClick={handleRemoveClick}
						slug="awardTitle"
						contentSlug={slug}
						handleModalVisibility={handleModalVisibility}
						formikObj={formik}
						handleTitleChange={handleTitleChange}
					/>
					<p className="mb-0">{translate('Resume.awardSubtitle')}</p>
				</div>
				{fieldVisibility && (
					<div className="build-resume-edit-fields">
						{!checkLimits && (
							<Row>
								<Col xl="6">
									<div className="text-start">
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('PlaceHolder.title')}</Form.Label>
											<Form.Control
												id="title"
												name="title"
												type="text"
												placeholder={translate('PlaceHolder.enterTitle')}
												onChange={(event: any) => {
													onHandleChange(slug, event, data?.length - 1);
													formik.setFieldValue(event.target.name, event.target.value);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.title}
												maxLength={100}
											/>
											{renderError('title')}
										</Form.Group>
									</div>
								</Col>
								<Col xl="6">
									<div className="text-start">
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('PlaceHolder.year')}</Form.Label>
											<ReactSelect
												className="basic-single"
												classNamePrefix="select"
												isSearchable
												name="year"
												options={generateYearOptions()}
												getOptionValue={(option: any) => option.value}
												getOptionLabel={(option: any) => option.label}
												onChange={(value: any) => {
													const eventObj = {
														target: {
															name: 'year',
															value: value?.value,
															innerText: value?.label,
															type: 'dropdown',
														},
													};
													onHandleChange(slug, eventObj, data?.length - 1);
													formik.setFieldValue('year', value?.value);
												}}
												onBlur={formik.handleBlur}
												autoFocus={false}
												placeholder={translate('PlaceHolder.selectYear')}
												value={generateYearOptions()?.find(
													(item: any) => item.value === formik.values.year,
												)}
											/>
											{renderError('year')}
										</Form.Group>
									</div>
								</Col>
								<Col xl="12">
									<div className="text-start">
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">
												{translate('PlaceHolder.description')}
											</Form.Label>
											<Form.Control
												as="textarea"
												name="description"
												maxLength={TEXTAREA_TEXT_LIMIT}
												placeholder={translate('PlaceHolder.enterDescHere')}
												style={{ height: '156px' }}
												onChange={(event: any) => {
													onHandleChange(slug, event, data?.length - 1);
													formik.setFieldValue(event.target.name, event.target.value);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.description}
											/>
											<div className="alert-msg">{renderError('description')}</div>
											<div className="position-absolute end-0" style={{ bottom: '-20px' }}>
												<p className="text-end mb-0">
													{formik.values.description?.length}/{TEXTAREA_TEXT_LIMIT}
												</p>
											</div>
										</Form.Group>
									</div>
								</Col>
								{showAwardImage && (
									<Col xl="12" className="mt-3">
										<Form.Group className="input-field text-center" controlId="formFile">
											<label className="upload-field d-flex align-items-center justify-content-center w-100">
												<i className="ri-upload-line me-2"></i>
												{data?.[data.length - 1]?.logo
													? translate('Resume.logoUploaded')
													: translate('Resume.uploadLogo')}
												<Form.Control
													type="file"
													name="logo"
													onChange={(event) =>
														handleFileChange({ slug, event, index: data?.length - 1 })
													}
												/>
											</label>
										</Form.Group>
										{renderError('logo')}
									</Col>
								)}
								<Col xl="12" className="mt-3">
									<AddMoreButton title={translate('Resume.addAwards')} />
								</Col>
							</Row>
						)}
						{data?.length > 0 &&
							data?.map((field: any, index: number) => {
								const { title, year, description, logo } = field ?? {};
								if (data?.length > 1 && data?.length - 1 !== index) {
									return (
										<InfoCard
											key={index}
											content={{
												title,
												subtitle: `in ${year}`,
												description,
												logo,
											}}
											slug={slug}
											index={index}
											handleRemove={handleRemoveClick}
											handleModalVisibility={handleModalVisibility}
										/>
									);
								}
							})}
					</div>
				)}
			</Form>
		</div>
	);
};

export default Achievements;
